// ────────────────────────────────────────────────────────────────────
// presence.ts — the frontend half of "honest presence" (roadmap 2.3): a
// small heartbeat that tells the backend registry (server/presence.py)
// "this user is on this page right now", so TeamChatDock's presence strip
// can show a truthful dot instead of the old hardcoded #15803D one that was
// deliberately removed months ago (see TeamChatDock.test.tsx's "no false
// presence" test — now extended to assert the dot DOES appear once it's
// heartbeat-backed).
//
// `PresenceBeacon` must be mounted EXACTLY ONCE, somewhere that covers
// every authenticated route. AppShell fits: it's the layout element for
// every route under RequireAuth (nested routing means it does NOT remount
// when only the <Outlet> content changes on navigation), and it's already
// where TeamChatDockMounter lives. useLocation() inside this component
// reacts to route changes on its own — it doesn't need to be re-mounted
// per screen.
// ────────────────────────────────────────────────────────────────────
import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { apiClient, getActiveTenant, getCachedUser, getToken, STAFF_ROLES } from "./api";

const BEAT_INTERVAL_MS = 30_000;

// pathname -> Thai label, static routes first. Dynamic (":id") segments are
// handled in labelForPath below. Anything unmapped falls back to "ออนไลน์"
// rather than leaking a raw pathname the other side would never recognize.
const ROUTE_LABELS: Record<string, string> = {
  "/": "โต๊ะทำงาน",
  "/today": "ถาดงานวันนี้",
  "/queue": "คิวงาน",
  "/atlas": "แดชบอร์ด",
  "/submissions": "หน้างาน",
  "/files/queue": "ไฟล์ · รอจัดการ",
  "/files/assets": "ไฟล์ · ตาม asset",
  "/files/packages": "ไฟล์ · แพ็กเกจ",
  "/reviews": "รายการรอตรวจ",
  "/inbox": "กล่องข้อความ",
  "/reports": "หน้ารายงาน",
  "/settings": "หน้าตั้งค่า",
};

/** Derives the Thai label for the presence strip from a route pathname.
 *  Exported for the frontend test suite. */
export function labelForPath(pathname: string): string {
  const direct = ROUTE_LABELS[pathname];
  if (direct) return direct;
  // /assets/:id — "asset #<id>". The design brief's "กำลังดู asset" fallback
  // (for an unresolvable tag) isn't something this route-only map can ever
  // decide — it has no data-fetch of its own, only the URL — so an
  // /assets/:id match always yields the id-bearing label; a truly unmapped
  // path (never matches the regex) still lands on the generic "ออนไลน์" below.
  const asset = pathname.match(/^\/assets\/([^/]+)/);
  if (asset) return `asset #${asset[1]}`;
  return "ออนไลน์";
}

// Same gating liveEvents.ts's authHeaders() uses: no point beating pre-login
// (no token), and a staff caller who hasn't picked a tenant yet has nothing
// to register presence AGAINST — the backend would just 400 ("Select a
// tenant first", mirroring events.stream()) on every attempt. Customers are
// tenant-locked server-side, so they never need this check.
function canBeat(): boolean {
  if (!getToken()) return false;
  const user = getCachedUser();
  if (user && (STAFF_ROLES as readonly string[]).includes(user.role)) {
    return !!getActiveTenant();
  }
  return true;
}

/** Fire-and-forget: a failed beat (network blip, expired token, 400
 *  no-tenant) just means this entry goes stale a beat sooner than usual —
 *  never a user-visible error, so nothing here surfaces one. */
function fireBeat(page: string, label: string): void {
  if (!canBeat()) return;
  apiClient.presenceBeat({ page, label }).catch(() => { /* best-effort */ });
}

// Leave-on-teardown. Deliberately NOT `navigator.sendBeacon` despite the
// original design sketch calling for it: the Beacon API allows no custom
// headers whatsoever, and this app's auth is a bearer JWT that MUST ride an
// Authorization header (see liveEvents.ts's authHeaders() — same
// constraint). `fetch(..., { keepalive: true })` gives the same "survive
// the page tearing down" guarantee sendBeacon exists for, but keeps normal
// headers — every browser this portal targets (Chrome/Edge/Firefox/Safari)
// ships it. See the task report for this deliberate deviation.
function fireLeave(): void {
  const token = getToken();
  if (!token) return;
  const headers: Record<string, string> = {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };
  const user = getCachedUser();
  const tenant = getActiveTenant();
  if (tenant && user && (STAFF_ROLES as readonly string[]).includes(user.role)) {
    headers["X-Tenant-Id"] = tenant;
  }
  try {
    void fetch("/api/presence/beat", {
      method: "POST",
      headers,
      keepalive: true,
      body: JSON.stringify({ page: "", label: "", leave: true }),
    });
  } catch {
    /* best-effort teardown call — must never block/throw during unload */
  }
}

/** Mount ONCE, app-level (see module docstring). Sends a heartbeat
 *  immediately on mount and on every route change, then every 30s while
 *  the tab stays visible — paused entirely (no beat at all) while
 *  `document.visibilityState !== "visible"`, since a backgrounded tab isn't
 *  "looking at" anything and honest presence means never claiming
 *  otherwise. Fires an explicit leave on `pagehide` (tab close / navigate
 *  away from the app / reload). Renders nothing. */
export function PresenceBeacon(): null {
  const location = useLocation();
  // Mirrors the current route's page/label so the interval + visibility
  // listeners (which don't themselves depend on `location`) always beat
  // with the freshest value instead of one captured at mount.
  const pageRef = useRef(location.pathname);
  const labelRef = useRef(labelForPath(location.pathname));

  useEffect(() => {
    pageRef.current = location.pathname;
    labelRef.current = labelForPath(location.pathname);
    if (document.visibilityState === "visible") fireBeat(pageRef.current, labelRef.current);
  }, [location.pathname]);

  useEffect(() => {
    const interval = window.setInterval(() => {
      if (document.visibilityState === "visible") fireBeat(pageRef.current, labelRef.current);
    }, BEAT_INTERVAL_MS);

    // Beat immediately when the tab regains visibility — otherwise a user
    // tabbing back in could look "absent" for up to another 30s despite
    // being right there, which is the same kind of honesty gap this whole
    // feature exists to close.
    const onVisibility = () => {
      if (document.visibilityState === "visible") fireBeat(pageRef.current, labelRef.current);
    };
    document.addEventListener("visibilitychange", onVisibility);

    const onPageHide = () => fireLeave();
    window.addEventListener("pagehide", onPageHide);

    return () => {
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pagehide", onPageHide);
    };
  }, []);

  return null;
}
